import React, { useEffect, useState } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogContentText,
  DialogActions,
  TextField,
  Button,
  Alert,
  Box,
  Typography,
  CircularProgress,
} from '@mui/material';
import { deleteAccount } from '../lib/deleteAccount';
import { useDataContext } from '../data/DataProvider';
import { track } from '../utils/analytics';

// Confirmation de suppression de compte (App Store, guideline 5.1.1(v)).
//
// Deux choses doivent etre comprises avant de valider, et elles sont ecrites en
// clair plutot que sous-entendues :
//  - c'est definitif, rien ne se recupere ensuite ;
//  - l'achat App Store n'est PAS annule. Il appartient a l'identifiant Apple et
//    se restaure apres une nouvelle inscription. Sans cette phrase, la
//    suppression genere des demandes de remboursement.
//
// La saisie de DELETE n'est pas de la friction gratuite : le bouton est a cote
// d'un "Reset user data" qui, lui, se rattrape.

const CONFIRM_WORD = 'DELETE';

// Le clavier iOS recouvre le bas de l'ecran sans redimensionner la page : le champ
// de confirmation, place en fin de dialogue, passait dessous et on tapait en
// aveugle. visualViewport, lui, rend la hauteur reellement visible clavier
// deploye ; on y borne la hauteur du dialogue. Constate sur iPhone le 30/07/2026.
const useVisibleHeight = () => {
  const [height, setHeight] = useState(() =>
    typeof window === 'undefined' ? null : window.visualViewport?.height ?? null
  );

  useEffect(() => {
    const viewport = typeof window === 'undefined' ? null : window.visualViewport;
    if (!viewport) return undefined;
    const update = () => setHeight(viewport.height);
    viewport.addEventListener('resize', update);
    return () => viewport.removeEventListener('resize', update);
  }, []);

  return height;
};

const DeleteAccountDialog = ({ open, onClose, showPurchaseNote = false, showPatreonNote = false }) => {
  const { db } = useDataContext();
  const [confirmation, setConfirmation] = useState('');
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState(null);
  const visibleHeight = useVisibleHeight();

  useEffect(() => {
    if (!open) {
      setConfirmation('');
      setBusy(false);
      setError(null);
    }
  }, [open]);

  const confirmed = confirmation.trim().toUpperCase() === CONFIRM_WORD;

  const handleDelete = async () => {
    if (!confirmed || busy) return;
    setBusy(true);
    setError(null);
    try {
      // Trace avant l'appel : resetAnalytics detache l'identite juste apres.
      track('account_deletion_confirmed');
      await deleteAccount(db);
      // Le compte n'existe plus. Le rechargement repart sur une session invitee
      // neuve, comme apres une premiere installation.
      window.location.reload();
    } catch (err) {
      setBusy(false);
      if (err.message === 'no_session') {
        setError('You are not signed in on this device.');
      } else if (err.status === 503) {
        setError('Account deletion is temporarily unavailable. Please try again later.');
      } else {
        setError("We couldn't delete your account. Nothing was changed. Please try again.");
      }
    }
  };

  return (
    <Dialog
      open={open}
      onClose={busy ? undefined : onClose}
      fullWidth
      maxWidth="xs"
      // Ancre en haut : centre, le dialogue garderait sa moitie basse, donc le
      // champ de saisie, sous le clavier.
      sx={{ '& .MuiDialog-container': { alignItems: 'flex-start' } }}
      PaperProps={{
        sx: {
          mt: 2,
          mb: 'var(--safe-area-inset-bottom, env(safe-area-inset-bottom, 0px))',
          maxHeight: visibleHeight ? `${visibleHeight - 32}px` : undefined,
        },
      }}
    >
      <DialogTitle>Delete your account?</DialogTitle>
      <DialogContent>
        <DialogContentText sx={{ mb: 2 }}>
          {/* Enumeration volontairement neutre : les cas particuliers (achat,
              Patreon) ont leurs propres encarts juste dessous, et nommer une
              plateforme de paiement tierce ou un acces par invitation dans un
              build natif public n'a pas lieu d'etre. */}
          This permanently deletes your upScale account and everything stored with it: your
          settings, saved patterns and presets, and any access attached to it. This cannot be
          undone, on this device or any other.
        </DialogContentText>

        {showPurchaseNote && (
          <Alert severity="info" sx={{ mb: 2 }}>
            Your App Store purchase is not cancelled and not refunded. It stays attached to your
            Apple ID. If you create a new account later, tap "Restore purchases" to get it back.
          </Alert>
        )}

        {showPatreonNote && (
          <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
            Your Patreon membership is not cancelled either. Deleting your account only removes
            the link between Patreon and upScale. Cancel the membership on Patreon if you want it
            to stop.
          </Typography>
        )}

        {error && (
          <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError(null)}>
            {error}
          </Alert>
        )}

        <TextField
          autoFocus
          fullWidth
          value={confirmation}
          onChange={(e) => setConfirmation(e.target.value)}
          // Le clavier s'installe apres le focus : on attend qu'il ait pousse le
          // viewport avant d'amener le champ dans la zone restee visible.
          onFocus={(event) => {
            const field = event.target;
            setTimeout(() => field.scrollIntoView({ block: 'center' }), 300);
          }}
          label={`Type ${CONFIRM_WORD} to confirm`}
          disabled={busy}
          inputProps={{ autoCapitalize: 'characters', autoCorrect: 'off', spellCheck: 'false' }}
        />
      </DialogContent>
      <DialogActions sx={{ px: 3, pb: 2 }}>
        <Button onClick={onClose} disabled={busy}>
          Keep my account
        </Button>
        <Button
          onClick={handleDelete}
          color="error"
          variant="contained"
          disabled={!confirmed || busy}
          sx={{ textTransform: 'none' }}
        >
          {busy ? (
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
              <CircularProgress size={16} color="inherit" />
              Deleting
            </Box>
          ) : (
            'Delete my account'
          )}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default DeleteAccountDialog;
